import { useRef, useEffect, useCallback } from "react";
import { saveToHistory } from "../api";
import { ChatAppResponse, SelectedFile } from "../api";
import { getToken } from "../authConfig";
import { IPublicClientApplication } from "@azure/msal-browser";

interface UseChatHistoryProps {
    sessionId: string;
    answers: [string, ChatAppResponse][];
    selectedFilters: Record<string, string[]>;
    selectedSourcefiles: SelectedFile[];
    client?: IPublicClientApplication;
    onHistorySaved?: () => void;
}

export const useChatHistory = ({
    sessionId,
    answers,
    selectedFilters,
    selectedSourcefiles,
    client,
    onHistorySaved
}: UseChatHistoryProps) => {
    const shouldUpdateHistory = useRef(false);

    const saveHistory = useCallback(async () => {
        // do not save empty answers
        if (answers.length === 0) return;

        try {
            const idToken = client ? await getToken(client) : undefined;

            const response = await saveToHistory(
                {
                    sessionId: sessionId,
                    chatContext: answers,
                    selectedFilters: selectedFilters,
                    selectedSourcefiles: selectedSourcefiles,
                },
                idToken
            );

            if (response.ok && onHistorySaved) {
                onHistorySaved();
            }
        } catch (error) {
            console.error('[saveHistory] Error', {
                sessionId,
                error: error instanceof Error ? error.message : String(error)
            });
        }
    }, [sessionId, answers, selectedFilters, selectedSourcefiles, client, onHistorySaved]);

    useEffect(() => {
        if (shouldUpdateHistory.current) {
            saveHistory();
            shouldUpdateHistory.current = false;
        }
    }, [answers, saveHistory]);

    const triggerHistorySave = useCallback(() => {
        shouldUpdateHistory.current = true;
    }, []);

    return { triggerHistorySave, shouldUpdateHistoryRef: shouldUpdateHistory };
};
